import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type {
  GetNotificationsRequest,
  GetNotificationsCountRequest,
  DismissNotificationRequest,
  DismissNotificationResponse,
  UnreadNotificationRequest,
  UnreadNotificationResponse,
  GetNotificationsResponse,
  GetNotificationsCountResponse,
} from "@ServerTypes/notifications/notifications.schema";
import {
  setNotificationsData,
  setUnreadNotificationsCount,
  setShowNotification,
  readNotificationsData,
  unreadNotificationsData,
  setNotificationsStatusesData,
} from "../reducers/notification";

export {
  setNotificationsData,
  setUnreadNotificationsCount,
  setShowNotification,
  readNotificationsData,
  unreadNotificationsData,
  setNotificationsStatusesData,
};

export async function getNotificationsList(skip = 0, take = 20, signal?: AbortSignal) {
  const data: GetNotificationsRequest = {
    value: {
      skip,
      take,
      requiresCounts: true,
      notificationType: ["NOTIFICATION_UNREAD"],
    },
  };

  // NOT A BUG — deliberate, recorded so nobody "tightens" it. `GetNotificationsResponse` is the
  // two-member union of the route's two modes (notifications.schema.ts:346): the default page
  // `{ status, result, count? }` and the header-bell `{ new, previous }`. This call never sets
  // `value.source`, so it always receives the page, and `NotificationsPage`
  // (notifications.schema.ts:489) would be the tighter type. It is NOT used here on purpose: the
  // union is what `isNotificationPage` narrows (components/Notification/useAllNotification.ts:58-60,
  // applied at :119); tightening this generic would make that guard redundant, so the two have to
  // change together. Nothing is broken either way — the union is a correct superset of what arrives.
  const response = await axiosWrapper.post<GetNotificationsResponse>(
    `${getBackendUrl()}/notifications/getNotifications`,
    data,
    { ...(signal !== undefined && { signal }) },
  );
  return response.data;
}

export function getNotificationsCount(filterType: GetNotificationsCountRequest["filter"]) {
  const data: GetNotificationsCountRequest = {
    filter: filterType,
  };
  return async (dispatch: AppDispatch) => {
    try {
      const response = await axiosWrapper.post<GetNotificationsCountResponse>(
        `${getBackendUrl()}/notifications/getNotificationsCount`,
        data,
      );

      if (response.data.status) {
        dispatch(setUnreadNotificationsCount(response.data.data?.count ?? 0));
      }
    } catch (err) {
      console.log(err);
      Promise.reject(err);
    }
  };
}

export async function readNotification(idsArr: DismissNotificationRequest["ids"]) {
  const data: DismissNotificationRequest = {
    ids: idsArr,
  };

  try {
    const response = await axiosWrapper.post<DismissNotificationResponse>(
      `${getBackendUrl()}/notifications/dismissNotification`,
      data,
    );

    return response.data.status;
  } catch (err) {
    console.log(err);
    Promise.reject(err);
    // The request failed, so nothing was marked read/unread: report that the same way the server's
    // own `status: false` does. Every caller reads the result as a boolean (`if (response) ...`),
    // so falling off the end here handed them `undefined` for exactly this meaning.
    return false;
  }
}

export async function readNotificationsInRange(fromDate?: number, toDate?: number) {
  const data: DismissNotificationRequest = {
    fromDate,
    toDate,
  };

  try {
    const response = await axiosWrapper.post<DismissNotificationResponse>(
      `${getBackendUrl()}/notifications/dismissNotification`,
      data,
    );

    return response.data.status;
  } catch (err) {
    console.log(err);
    Promise.reject(err);
    // The request failed, so nothing was marked read/unread: report that the same way the server's
    // own `status: false` does. Every caller reads the result as a boolean (`if (response) ...`),
    // so falling off the end here handed them `undefined` for exactly this meaning.
    return false;
  }
}

export async function unreadNotification(idsArr: UnreadNotificationRequest["ids"]) {
  const data: UnreadNotificationRequest = {
    ids: idsArr,
  };

  try {
    const response = await axiosWrapper.post<UnreadNotificationResponse>(
      `${getBackendUrl()}/notifications/unreadNotification`,
      data,
    );

    return response.data.status;
  } catch (err) {
    console.log(err);
    Promise.reject(err);
    // The request failed, so nothing was marked read/unread: report that the same way the server's
    // own `status: false` does. Every caller reads the result as a boolean (`if (response) ...`),
    // so falling off the end here handed them `undefined` for exactly this meaning.
    return false;
  }
}
